import { z } from 'zod';
import { optionalString, requiredString } from './common';
import { type PrintTemplateDocument, printTemplateDocumentSchema } from './print-template';

const printTemplateFields = {
  name: requiredString('Name'),
  description: optionalString(),
  document: printTemplateDocumentSchema,
};

export const createPrintTemplateSchema = z.object(printTemplateFields);
export type CreatePrintTemplateInput = z.infer<typeof createPrintTemplateSchema>;

export const updatePrintTemplateSchema = createPrintTemplateSchema;
export type UpdatePrintTemplateInput = z.infer<typeof updatePrintTemplateSchema>;

/**
 * `POST /print/preview` — render an unsaved document.
 *
 * The builder's preview is produced by the *same* endpoint pipeline as a real
 * print job, from the draft in the form rather than from a stored row. That is
 * the point: there is one renderer, so the preview cannot disagree with the
 * output. A preview that is a second implementation of the layout is a preview
 * that will eventually lie.
 */
export const printPreviewSchema = z.object({
  document: printTemplateDocumentSchema,
  /** Preview against a real patient. Omitted renders the built-in sample report. */
  patient_id: z.coerce.number().int().positive().optional(),
});
export type PrintPreviewInput = z.infer<typeof printPreviewSchema>;

/** `GET /print-templates` returns these; the document is parsed, never raw JSON. */
export interface PrintTemplateView {
  id: number;
  name: string;
  description: string | null;
  /** The template a print job falls back to. Exactly one row carries it. */
  is_default: boolean;
  document: PrintTemplateDocument;
  created_at: string;
  updated_at: string;
}
